export function calculateWeightedPercentage(scores, assessments) {
  if (!Array.isArray(assessments) || assessments.length === 0) {
    return 0;
  }

  const total = assessments.reduce((sum, assessment, index) => {
    const rawScore = Number(scores[index]);
    const numericScore = Number.isFinite(rawScore) ? rawScore : 0;
    const clampedScore = Math.max(0, Math.min(assessment.maxMarks, numericScore));

    if (!assessment.maxMarks) {
      return sum;
    }

    return sum + (clampedScore / assessment.maxMarks) * assessment.weight * 100;
  }, 0);

  return Math.round((total + Number.EPSILON) * 100) / 100;
}

export function calculateSubjectScore(subjectName, scores, assessments) {
  if (subjectName === "Theory Of Knowledge") {
    const essay = Number(scores[0]) || 0;
    const exhibition = Number(scores[1]) || 0;
    return (essay * 2) + exhibition;
  }

  if (subjectName === "Extended Essay") {
    return Number(scores[0]) || 0;
  }

  if (subjectName === "Creativity, Activity, Service") {
    return null;
  }

  return calculateWeightedPercentage(scores, assessments);
}

export function resolveAwardedResult(score, boundaryEntry, scale = "numeric") {
  if (!boundaryEntry || score === null || score === undefined) {
    return null;
  }

  if (scale === "core") {
    const coreScale = ["E", "D", "C", "B", "A"];
    let awardedBand = null;

    coreScale.forEach((label) => {
      const cutoff = boundaryEntry[label];
      if (typeof cutoff === "number" && score >= cutoff) {
        awardedBand = label;
      }
    });

    return awardedBand;
  }

  let awardedGrade = null;

  for (let grade = 1; grade <= 7; grade += 1) {
    const cutoff = boundaryEntry[String(grade)] ?? boundaryEntry[grade];
    if (typeof cutoff === "number" && score >= cutoff) {
      awardedGrade = grade;
    }
  }

  return awardedGrade;
}

export function getPointsToNextGrade(score, boundaryEntry, awardedGrade, scale = "numeric") {
  if (!boundaryEntry || !awardedGrade) {
    return null;
  }

  if (scale === "core") {
    const coreScale = ["E", "D", "C", "B", "A"];
    const currentIndex = coreScale.indexOf(awardedGrade);
    if (currentIndex === -1 || currentIndex >= coreScale.length - 1) {
      return null;
    }

    const nextCutoff = boundaryEntry[coreScale[currentIndex + 1]];
    if (typeof nextCutoff !== "number") {
      return null;
    }

    return Math.max(0, Math.round((nextCutoff - score + Number.EPSILON) * 100) / 100);
  }

  if (awardedGrade >= 7) {
    return null;
  }

  const nextCutoff =
    boundaryEntry[String(awardedGrade + 1)] ?? boundaryEntry[awardedGrade + 1];

  if (typeof nextCutoff !== "number") {
    return null;
  }

  return Math.max(0, Math.round((nextCutoff - score + Number.EPSILON) * 100) / 100);
}

export function getPointsAboveBoundary(score, boundaryEntry, awardedGrade, scale = "numeric") {
  if (!boundaryEntry || !awardedGrade) {
    return null;
  }

  if (scale === "core") {
    const currentCutoff = boundaryEntry[awardedGrade];
    if (typeof currentCutoff !== "number") {
      return null;
    }

    return Math.max(0, Math.round((score - currentCutoff + Number.EPSILON) * 100) / 100);
  }

  const currentCutoff =
    boundaryEntry[String(awardedGrade)] ?? boundaryEntry[awardedGrade];

  if (typeof currentCutoff !== "number") {
    return null;
  }

  return Math.max(0, Math.round((score - currentCutoff + Number.EPSILON) * 100) / 100);
}

export function getBoundaryBands(boundaryEntry, scale = "numeric", maxScore = 100) {
  if (!boundaryEntry) {
    return [];
  }

  const labels = scale === "core" ? ["E", "D", "C", "B", "A"] : [1, 2, 3, 4, 5, 6, 7];
  const totalSpan = maxScore + 1;

  return labels
    .map((label, index) => {
      const min = boundaryEntry[String(label)] ?? boundaryEntry[label];
      if (typeof min !== "number") {
        return null;
      }

      const nextLabel = labels[index + 1];
      const nextMin =
        nextLabel !== undefined
          ? boundaryEntry[String(nextLabel)] ?? boundaryEntry[nextLabel]
          : null;
      const endExclusive = typeof nextMin === "number" ? nextMin : maxScore + 1;
      const max = typeof nextMin === "number" ? Math.max(min, nextMin - 1) : maxScore;

      return {
        label,
        min,
        max,
        width: Math.max(0, ((endExclusive - min) / totalSpan) * 100),
      };
    })
    .filter(Boolean);
}
